import { useRef } from "react";
import { RotateCcw, X } from "lucide-react";
import type {
  CheckinAuditEntry,
  CheckinStatus,
} from "@/features/checkin/types";
import {
  filterLifecycleAuditEvents,
  formatCheckinAuditOperation,
  getCheckinAuditOperation,
  lifecycleHistoryUndoReason,
} from "@/features/checkin/pageDisplay";
import { useDialogFocus } from "@/hooks/useDialogFocus";

export function CheckinLifecycleHistoryDrawer({
  open,
  events,
  status,
  canWrite,
  loading,
  error,
  pending,
  onUndo,
  onClose,
}: {
  open: boolean;
  events: CheckinAuditEntry[];
  status: CheckinStatus;
  canWrite: boolean;
  loading: boolean;
  error: boolean;
  pending: boolean;
  onUndo: (event: CheckinAuditEntry) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(dialogRef, onClose, {
    enabled: open,
    initialFocusRef: closeRef,
  });
  if (!open) return null;
  const lifecycle = filterLifecycleAuditEvents(events);
  return (
    <div className="checkin-history-layer" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="checkin-history-drawer detail-modal history-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Histórico da carga"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-modal-head">
          <div>
            <span>HISTÓRICO DA CARGA</span>
            <h2 id="checkin-lifecycle-history-title">
              Fechamentos e reaberturas
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar histórico da carga"
          >
            <X size={18} />
          </button>
        </header>
        <div className="detail-modal-body">
          <div className="checkin-history-events history-timeline">
            {loading ? <p>Carregando histórico…</p> : null}
            {error ? (
              <p role="alert">
                Não foi possível carregar o histórico da carga.
              </p>
            ) : null}
            {!loading && !error && !lifecycle.length ? (
              <p>Nenhum fechamento ou reabertura registrado.</p>
            ) : null}
            {!loading &&
              !error &&
              lifecycle.map((event) => {
                const reason = lifecycleHistoryUndoReason(
                  event,
                  events,
                  status,
                  canWrite,
                );
                return (
                  <article key={event.id} className="history-event">
                    <div>
                      <strong>
                        {formatCheckinAuditOperation(
                          getCheckinAuditOperation(event),
                        )}
                      </strong>
                      <span>
                        {event.changed_by_name ?? "Usuário"} ·{" "}
                        {new Intl.DateTimeFormat("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                          timeZone: "America/Sao_Paulo",
                        }).format(new Date(event.changed_at))}
                      </span>
                      {canWrite ? (
                        <>
                          <button
                            type="button"
                            className="history-event-undo"
                            disabled={pending || Boolean(reason)}
                            onClick={() => onUndo(event)}
                          >
                            <RotateCcw size={16} /> Desfazer
                          </button>
                          {reason ? (
                            <p className="history-event-reason">{reason}</p>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
          </div>
        </div>
      </section>
    </div>
  );
}
